import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import axios from "axios";

// reducer - state management
import { LOGIN, LOGOUT } from "store/actions";
import accountReducer from "store/accountReducer";

// project imports
import Loader from "ui-component/Loader";
import { useDispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

// Create direct axios instance for backend calls
const backendAPI = axios.create({
  baseURL: "https://api.kuga59.co/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const [isInitialized, setIsInitialized] = useState(false);
  const reduxDispatch = useDispatch();

  // Force logout and notify across the app when session is invalidated
  const handleForceLogout = useCallback(() => {
    localStorage.removeItem("serviceToken");

    dispatch({ type: LOGOUT });

    // Show notification explaining why the session ended
    reduxDispatch(
      openSnackbar({
        open: true,
        message: "คุณถูกออกจากระบบเนื่องจากมีการเข้าสู่ระบบจากอุปกรณ์อื่น",
        variant: "alert",
        alert: {
          color: "warning",
        },
        close: true,
      })
    );

    // Clear local storage data and redirect to login page
    window.localStorage.clear();
    window.location.href = "/login";
  }, [dispatch, reduxDispatch]);

  useEffect(() => {
    const init = async () => {
      try {
        // Check if admin has a valid token stored
        const token = localStorage.getItem("serviceToken");
        if (token) {
          try {
            // Call backend API for admin authentication directly
            const response = await backendAPI.get("/api/admin/account/me", {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.data && response.data.user) {
              const user = response.data.user;

              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: {
                    id: user.id,
                    username: user.username,
                    name: user.name,
                    role: user.role,
                    canViewDashboard: !!user.canViewDashboard,
                    canViewFinance: !!user.canViewFinance,
                    canViewDeposit: !!user.canViewDeposit,
                    canViewWithdraw: !!user.canViewWithdraw,
                    canManageWithdraw: !!user.canManageWithdraw,
                    canViewAdmin: !!user.canViewAdmin,
                    canCreateAdmin: !!user.canCreateAdmin,
                    canEditAdmin: !!user.canEditAdmin,
                    canViewCreditCode: !!user.canViewCreditCode,
                    canCreateCreditCode: !!user.canCreateCreditCode,
                    canViewCreditCodeList: !!user.canViewCreditCodeList,
                    canViewCreditCodeUsed: !!user.canViewCreditCodeUsed,
                    canViewPromotion: !!user.canViewPromotion,
                    canViewCampaign: !!user.canViewCampaign,
                    canViewRanking: !!user.canViewRanking,
                    canManageRankingSetting: !!user.canManageRankingSetting,
                    canViewFriendInvite: !!user.canViewFriendInvite,
                    canViewFriendInviteHistory:
                      !!user.canViewFriendInviteHistory,
                    canManageFriendInviteSetting:
                      !!user.canManageFriendInviteSetting,
                    canViewCashback: !!user.canViewCashback,
                    canViewCashbackHistory: !!user.canViewCashbackHistory,
                    canManageCashbackSetting: !!user.canManageCashbackSetting,
                    canViewTurnSetting: !!user.canViewTurnSetting,
                    canViewWheelPrize: !!user.canViewWheelPrize,
                    canViewDepositSummary: !!user.canViewDepositSummary,
                    canViewWithdrawSummary: !!user.canViewWithdrawSummary,
                    canViewWebSetting: !!user.canViewWebSetting,
                    canViewBannerSetting: !!user.canViewBannerSetting,
                    canViewPopupSetting: !!user.canViewPopupSetting,
                    canViewBankSetting: !!user.canViewBankSetting,
                    canViewPaymentSetting: user.canViewPaymentSetting !== false,
                    canManageTmnAccount: !!user.canManageTmnAccount,
                    canManageScbAccount: !!user.canManageScbAccount,
                    canViewCustomer: !!user.canViewCustomer,
                    canEditCustomerUsername: !!user.canEditCustomerUsername,
                    canManageCustomerCreditAdd: !!(
                      user.canManageCustomerCreditAdd ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCashAdd: !!(
                      user.canManageCustomerCashAdd ??
                      user.canManageCustomerCreditAdd ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCreditAdjust: !!(
                      user.canManageCustomerCreditAdjust ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCashAdjust: !!(
                      user.canManageCustomerCashAdjust ??
                      user.canManageCustomerCreditAdjust ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCreditDeduct: !!(
                      user.canManageCustomerCreditDeduct ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCashDeduct: !!(
                      user.canManageCustomerCashDeduct ??
                      user.canManageCustomerCreditDeduct ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerCredit: !!(
                      user.canManageCustomerCredit ??
                      user.canManageCustomerCreditAdd ??
                      user.canManageCustomerCreditAdjust ??
                      user.canManageCustomerCreditDeduct ??
                      user.canManageCustomerCashAdd ??
                      user.canManageCustomerCashAdjust ??
                      user.canManageCustomerCashDeduct
                    ),
                    canManageCustomerCash: !!(
                      user.canManageCustomerCash ??
                      user.canManageCustomerCashAdd ??
                      user.canManageCustomerCashAdjust ??
                      user.canManageCustomerCashDeduct ??
                      user.canManageCustomerCredit
                    ),
                    canManageCustomerBank: !!user.canManageCustomerBank,
                    canManageCustomerDiamond: !!user.canManageCustomerDiamond,
                    canManageWheelSetting: !!user.canManageWheelSetting,
                    canViewWheelHistory: !!user.canViewWheelHistory,
                    canManageBoxSetting: !!user.canManageBoxSetting,
                    canViewBoxHistory: !!user.canViewBoxHistory,
                    canManageTwoFactor: !!user.canManageTwoFactor,
                    twoFactorEnabled: !!user.twoFactorEnabled,
                  },
                },
              });
            } else {
              throw new Error("Invalid token");
            }
          } catch (err) {
            console.error("Token verification error:", err);
            // Check if it's a session invalidation
            if (err.response?.data?.code === "SESSION_INVALIDATED") {
              console.log("Admin session invalidated - logged in elsewhere");
              handleForceLogout();
              return;
            }

            // Token is invalid, remove it
            localStorage.removeItem("serviceToken");
            dispatch({
              type: LOGOUT,
            });
          }
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error("Admin token verification failed:", err);
        localStorage.removeItem("serviceToken");
        dispatch({
          type: LOGOUT,
        });
      } finally {
        setIsInitialized(true);
      }
    };

    init();
  }, [reduxDispatch, handleForceLogout]);

  const login = async (username, password, twoFactorCode) => {
    try {
      // Call backend admin login API directly
      const payload = {
        username,
        password,
      };

      if (twoFactorCode) {
        payload.twoFactorCode = twoFactorCode;
      }

      const response = await backendAPI.post(
        "/api/admin/account/login",
        payload
      );

      const { serviceToken, user } = response.data;

      if (!serviceToken || !user) {
        throw new Error("Invalid response from server");
      }

      // Store the token
      localStorage.setItem("serviceToken", serviceToken);

      // Dispatch login action
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            id: user.id,
            username: user.username,
            name: user.name,
            role: user.role,
            canViewDashboard: !!user.canViewDashboard,
            canViewFinance: !!user.canViewFinance,
            canViewDeposit: !!user.canViewDeposit,
            canViewWithdraw: !!user.canViewWithdraw,
            canManageWithdraw: !!user.canManageWithdraw,
            canViewAdmin: !!user.canViewAdmin,
            canCreateAdmin: !!user.canCreateAdmin,
            canEditAdmin: !!user.canEditAdmin,
            canViewCreditCode: !!user.canViewCreditCode,
            canCreateCreditCode: !!user.canCreateCreditCode,
            canViewCreditCodeList: !!user.canViewCreditCodeList,
            canViewCreditCodeUsed: !!user.canViewCreditCodeUsed,
            canViewPromotion: !!user.canViewPromotion,
            canViewCampaign: !!user.canViewCampaign,
            canViewRanking: !!user.canViewRanking,
            canManageRankingSetting: !!user.canManageRankingSetting,
            canViewFriendInvite: !!user.canViewFriendInvite,
            canViewFriendInviteHistory: !!user.canViewFriendInviteHistory,
            canManageFriendInviteSetting: !!user.canManageFriendInviteSetting,
            canViewCashback: !!user.canViewCashback,
            canViewCashbackHistory: !!user.canViewCashbackHistory,
            canManageCashbackSetting: !!user.canManageCashbackSetting,
            canViewDepositSummary: !!user.canViewDepositSummary,
            canViewWithdrawSummary: !!user.canViewWithdrawSummary,
            canViewWebSetting: !!user.canViewWebSetting,
            canViewBannerSetting: !!user.canViewBannerSetting,
            canViewPopupSetting: !!user.canViewPopupSetting,
            canViewBankSetting: !!user.canViewBankSetting,
            canViewPaymentSetting: user.canViewPaymentSetting !== false,
            canManageTmnAccount: !!user.canManageTmnAccount,
            canManageScbAccount: !!user.canManageScbAccount,
            canViewCustomer: !!user.canViewCustomer,
            canEditCustomerUsername: !!user.canEditCustomerUsername,
            canManageCustomerCreditAdd: !!(
              user.canManageCustomerCreditAdd ?? user.canManageCustomerCredit
            ),
            canManageCustomerCashAdd: !!(
              user.canManageCustomerCashAdd ??
              user.canManageCustomerCreditAdd ??
              user.canManageCustomerCredit
            ),
            canManageCustomerCreditAdjust: !!(
              user.canManageCustomerCreditAdjust ?? user.canManageCustomerCredit
            ),
            canManageCustomerCashAdjust: !!(
              user.canManageCustomerCashAdjust ??
              user.canManageCustomerCreditAdjust ??
              user.canManageCustomerCredit
            ),
            canManageCustomerCreditDeduct: !!(
              user.canManageCustomerCreditDeduct ?? user.canManageCustomerCredit
            ),
            canManageCustomerCashDeduct: !!(
              user.canManageCustomerCashDeduct ??
              user.canManageCustomerCreditDeduct ??
              user.canManageCustomerCredit
            ),
            canManageCustomerCredit: !!(
              user.canManageCustomerCredit ??
              user.canManageCustomerCreditAdd ??
              user.canManageCustomerCreditAdjust ??
              user.canManageCustomerCreditDeduct ??
              user.canManageCustomerCashAdd ??
              user.canManageCustomerCashAdjust ??
              user.canManageCustomerCashDeduct
            ),
            canManageCustomerCash: !!(
              user.canManageCustomerCash ??
              user.canManageCustomerCashAdd ??
              user.canManageCustomerCashAdjust ??
              user.canManageCustomerCashDeduct ??
              user.canManageCustomerCredit
            ),
            canManageCustomerBank: !!user.canManageCustomerBank,
            canManageCustomerDiamond: !!user.canManageCustomerDiamond,
            canViewTurnSetting: !!user.canViewTurnSetting,
            canViewWheelPrize: !!user.canViewWheelPrize,
            canManageWheelSetting: !!user.canManageWheelSetting,
            canViewWheelHistory: !!user.canViewWheelHistory,
            canManageBoxSetting: !!user.canManageBoxSetting,
            canViewBoxHistory: !!user.canViewBoxHistory,
            canManageTwoFactor: !!user.canManageTwoFactor,
            twoFactorEnabled: !!user.twoFactorEnabled,
          },
        },
      });

      return response.data;
    } catch (error) {
      console.error("Login failed:", error);

      // Clear any stored token on login failure
      localStorage.removeItem("serviceToken");

      if (error.response) {
        const { message, code } = error.response.data || {};
        const authError = new Error(message || "Login failed");
        authError.code = code;
        authError.status = error.response.status;
        if (code === "TWO_FACTOR_REQUIRED") {
          authError.twoFactorRequired = true;
        }
        if (code === "TWO_FACTOR_INVALID") {
          authError.twoFactorInvalid = true;
        }
        throw authError;
      }

      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("serviceToken");
      if (token) {
        await backendAPI.post(
          "/api/admin/account/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("serviceToken");
      dispatch({ type: LOGOUT });
      window.localStorage.clear();
      window.location.href = "/login";
    }
  };

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
